/**
 * **PIN luring** — supaya tablet yang menyala tanpa server tetap bisa dibuka kasirnya (K13).
 *
 * ### ⛔⛔⛔ Yang TIDAK dilakukan berkas ini: menurunkan `pin_hash` dari server
 * Aturan outlet-server sudah menyatakannya sejak awal — *"⛔ PIN tak pernah ikut turun"*. Hash yang
 * dikirim ke perangkat adalah kredensial seluruh outlet dalam satu berkas: yang membawa pulang satu
 * tablet membawa pulang PIN semua orang, dan bisa menebaknya tanpa jaringan, tanpa jejak, secepat
 * perangkat kerasnya.
 *
 * ⇒ Yang dipakai di sini **verifier yang dicetak di tablet itu sendiri**, dari PIN yang baru saja
 *   diketik kasir pada login DARING yang berhasil. Tak ada apa pun yang turun dari server; yang
 *   ada cuma jejak lokal dari sesuatu yang memang sudah pernah diketik di mesin itu.
 *
 * ⇒ Konsekuensinya disengaja: **hanya staf yang pernah login daring di perangkat INI** yang bisa
 *   login luring di sini. Kasir baru yang belum pernah menyentuh tablet ini tak bisa masuk saat
 *   internet mati — dan itu jauh lebih baik daripada tablet yang menerima PIN siapa pun.
 *
 * ### ⚠️ Kenapa PBKDF2, bukan scrypt seperti di server
 * Server memakai scrypt (`node:crypto`, memory-hard). Peramban tak punya scrypt di WebCrypto —
 * yang ada PBKDF2. ⛔ Memakai pustaka scrypt WASM berarti menambah dependensi besar ke jalur boot
 * kasir demi hash yang **bukan** kredensial sistem: ia cuma verifier lokal berumur 7 hari, dan
 * pertahanan sebenarnya di sini adalah **penguncian**, bukan biaya hash.
 *
 * ⚠️ Perbedaan format itu justru harus terlihat: yang di server adalah kredensialnya, yang di sini
 * bayangannya.
 *
 * ### ⛔ Yang TIDAK dijanjikan mekanisme ini
 * Pengikatan ke perangkat menahan verifier yang **diangkat sendirian** (satu kunci `localStorage`
 * yang terbaca lalu ditempel di tablet lain). Ia **tidak** menahan penyalinan seluruh isi
 * penyimpanan sekaligus — kode perangkatnya ikut tersalin. Yang menahan itu bukan berkas ini
 * melainkan **token perangkat**, yang tak pernah tinggal di peramban (lihat `api/session.ts`):
 * salinan sempurna pun tak bisa menyentuh server.
 *
 * ⇒ Batas jujurnya: yang dijaga di sini adalah **layar**, bukan uang. Uang dijaga oleh gerbang yang
 *   tetap hidup di server — sesi kas tak bisa dibuka luring, dan setiap dokumen tetap dihitung
 *   ulang saat mendarat.
 */
import { MAX_PIN_ATTEMPTS, OFFLINE_LOGIN_MAX_AGE_MS, PIN_LOCK_MS, } from '@osbpos/contract';
/**
 * Iterasi PBKDF2-SHA256. 600.000 = anjuran OWASP 2023.
 *
 * ⚠️ Terasa ±0,5–1,5 detik di tablet murah — dan itu dibayar **sekali per login**, bukan per
 * ketukan. Menurunkannya berarti penebak ikut dipercepat dengan faktor yang sama.
 */
const ITERATIONS = 600_000;
const KEYLEN_BITS = 256;
/**
 * ⛔ Perangkat & kode staf ikut jadi bahan turunan, bukan sekadar label di sebelahnya.
 *
 * Label bisa disunting; bahan turunan tidak. Tanpa ini, isi `localStorage` satu tablet yang bocor
 * bisa disalin ke tablet lain (atau ditempel di nama staf lain) dan tetap berlaku — satu kebocoran
 * jadi kunci seluruh armada.
 */
function material(i) {
    return `osbpos-offline-pin ${i.staffCode} ${i.deviceCode} ${i.pin}`;
}
function b64(buf) {
    return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function fromB64(s) {
    return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}
async function derive(input, salt, iterations) {
    const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(material(input)), 'PBKDF2', false, ['deriveBits']);
    const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', salt: salt, iterations, hash: 'SHA-256' }, key, KEYLEN_BITS);
    return b64(bits);
}
/** ⚠️ Bandingkan **tanpa hubungan waktu** — `===` pada string membocorkan berapa byte yang cocok. */
function sameSecret(a, b) {
    const x = fromB64(a);
    const y = fromB64(b);
    if (x.length !== y.length)
        return false;
    let diff = 0;
    for (let i = 0; i < x.length; i += 1)
        diff |= (x[i] ?? 0) ^ (y[i] ?? 0);
    return diff === 0;
}
/**
 * Mencetak verifier dari PIN yang **baru saja terbukti benar di server**.
 *
 * ⛔ Hanya boleh dipanggil sesudah login daring BERHASIL. Mencetaknya dari PIN yang belum diperiksa
 * server berarti tablet menyimpan tebakan sebagai kebenaran, dan login luring berikutnya menerima
 * PIN yang tak pernah sah.
 */
export async function mintOfflineVerifier(input, now = new Date()) {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    return {
        staffCode: input.staffCode,
        salt: b64(salt.buffer),
        hash: await derive(input, salt, ITERATIONS),
        iterations: ITERATIONS,
        mintedAt: now.toISOString(),
        failedAttempts: 0,
        lockedUntil: null,
    };
}
/**
 * Memeriksa PIN terhadap verifier lokal, **dan** menegakkan kunci serta umur salinan.
 *
 * ⭐ Urutannya disengaja: kedaluwarsa & kunci diperiksa **sebelum** PIN-nya, supaya jawaban tak
 * pernah membocorkan "PIN-mu sebenarnya benar, cuma sedang terkunci".
 */
export async function checkOfflinePin(v, input, now = new Date()) {
    const t = now.getTime();
    /*
     * ⛔ Umur salinan ditegakkan lebih dulu, dan jawabannya BERBEDA dari "PIN salah". Kasir yang
     * disuruh menebak PIN-nya sendiri padahal yang dibutuhkan cuma sekali daring akan menghabiskan
     * jatah percobaannya, lalu terkunci karena masalah yang sama sekali bukan PIN.
     */
    if (t - Date.parse(v.mintedAt) > OFFLINE_LOGIN_MAX_AGE_MS) {
        return { outcome: 'expired', next: v };
    }
    if (v.lockedUntil !== null && Date.parse(v.lockedUntil) > t) {
        return { outcome: 'locked', next: v };
    }
    const cocok = sameSecret(v.hash, await derive(input, fromB64(v.salt), v.iterations));
    if (cocok) {
        return { outcome: 'ok', next: { ...v, failedAttempts: 0, lockedUntil: null } };
    }
    const failed = v.failedAttempts + 1;
    const terkunci = failed >= MAX_PIN_ATTEMPTS;
    return {
        outcome: terkunci ? 'locked' : 'wrong',
        next: {
            ...v,
            failedAttempts: failed,
            lockedUntil: terkunci ? new Date(t + PIN_LOCK_MS).toISOString() : null,
        },
    };
}
//# sourceMappingURL=offline-pin.js.map